import CreateRubricAIDialog from 'components/CreateAssignment/CreateRubricAIDialog';
import CreateRubricDialog from 'components/CreateAssignment/CreateRubricDialog';
import CreateRubricExistingDialog from 'components/CreateAssignment/CreateRubricExistingDialog';
import EditBaselineDialog from 'components/CreateAssignment/EditBaselineDialog';
import EditContentBankResourcesDialog from 'components/CreateAssignment/EditContentBankResourcesDialog';
import EditGradingSettingsDialog from 'components/CreateAssignment/EditGradingSettingsDialog';
import EditRubricDialog from 'components/CreateAssignment/EditRubricDialog';
import FeedbackSettingsDialog from 'components/CreateAssignment/FeedbackSettingsDialog';
import InlineCommentsSettingsDialog from 'components/CreateAssignment/InlineCommentsSettingsDialog';
import UploadContentBankResourcesDialog from 'components/CreateAssignment/UploadContentBankResourcesDialog';
import SelectRubricDialog from 'components/Rubrics/SelectRubricDialog';
import { useApp } from 'contexts/app';
import { useAppBar } from 'contexts/appBar';
import { useViewport } from 'contexts/viewport';
import { cloneDeep, omit } from 'lodash';
import Desktop from 'pages/CreateAssignment/desktop';
import Mobile from 'pages/CreateAssignment/mobile';
import { PageContext as CreatePageContext } from 'pages/CreateAssignment/PageProvider';
import Tablet from 'pages/CreateAssignment/tablet';
import { PageContext as EditPageContext } from 'pages/EditAssignment/PageProvider';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Loading, SplitButton, Stack, VideoDialog } from 'shell/components';

const PageLayout = ({ location }) => {
    const { isLoading, assignments, batch, submissions } = useApp();
    const { setNode } = useAppBar();
    const params = useParams();
    const { device } = useViewport();
    const { t } = useTranslation('assignment');

    const [videoDialog, setVideoDialog] = useState({ open: false, videoId: '' });

    const PageContext = location.pathname === '/assignments/new' ? CreatePageContext : EditPageContext;
    const props = useContext(PageContext);
    const {
        form,
        setForm,
        setErrors,
        selectRubricDialog,
        setSelectRubricDialog,
        createRubricDialog,
        setCreateRubricDialog,
        createRubricExistingDialog,
        setCreateRubricExistingDialog,
        createRubricAIDialog,
        setCreateRubricAIDialog,
        editRubricDialog,
        setEditRubricDialog,
        editBaselineDialog,
        setEditBaselineDialog,
        uploadContentBankResourcesDialog,
        setUploadContentBankResourcesDialog,
        editContentBankResourcesDialog,
        setEditContentBankResourcesDialog,
        feedbackSettingsDialog,
        setFeedbackSettingsDialog,
        inlineCommentsSettingsDialog,
        setInlineCommentsSettingsDialog,
        gradingSettingsDialog,
        setGradingSettingsDialog,
        isSaving = false,
        onClickSave,
    } = props;

    const assignment = useMemo(() => assignments?.find(({ id }) => id === params.id), [assignments, params.id]);

    const openVideoAssignmentCreation = useCallback(() => {
        setVideoDialog({ open: true, videoId: 'zSIaLCoDNfQ' });
    }, []);

    const openVideoFeedbackSettings = useCallback(() => {
        setVideoDialog({ open: true, videoId: 'fdUqnMyB6Rc' });
    }, []);

    const openVideoGradingSettings = () => {
        // video doesn't exist yet, replace with actual implementation when the video is made
        // setVideoDialog({ open: true, videoId: '' });
    };

    const closeSelectRubricDialog = useCallback(() => setSelectRubricDialog({ open: false }), [setSelectRubricDialog]);

    const handleClickAddRubric = useCallback(() => setCreateRubricDialog({ open: true }), [setCreateRubricDialog]);

    const handleClickUseRubric = useCallback(
        (rubric) => setEditRubricDialog({ open: true, customDefaultForm: rubric.rubric }),
        [setEditRubricDialog]
    );

    const closeCreateRubricDialog = useCallback(() => setCreateRubricDialog({ open: false }), [setCreateRubricDialog]);

    const handleClickCreateExisting = useCallback(
        () => setCreateRubricExistingDialog({ open: true }),
        [setCreateRubricExistingDialog]
    );

    const handleClickCreateAI = useCallback(() => setCreateRubricAIDialog({ open: true }), [setCreateRubricAIDialog]);

    const handleClickCreateManual = useCallback(
        () => setEditRubricDialog({ open: true, customDefaultForm: null }),
        [setEditRubricDialog]
    );

    const closeCreateRubricExistingDialog = useCallback(
        () => setCreateRubricExistingDialog({ open: false }),
        [setCreateRubricExistingDialog]
    );

    const closeCreateRubricAIDialog = useCallback(
        () => setCreateRubricAIDialog({ open: false }),
        [setCreateRubricAIDialog]
    );

    const closeEditRubricDialog = useCallback(
        () => setEditRubricDialog({ open: false, customDefaultForm: null }),
        [setEditRubricDialog]
    );

    const handleSaveRubric = useCallback(
        (rubric) => {
            setForm((current) => {
                const criterionIds = rubric.criteria.map(({ id }) => id);
                let newForm = cloneDeep({ ...current, rubric });

                if (current.baseline) {
                    setErrors((current) => ({ ...current, baseline: `(${t('needs-update', 'needs update')})` }));
                    newForm.baseline = { ...newForm.baseline, criteria: {} };
                }

                if (current.feedbackSettings.structure === 'templated') {
                    let newTemplated = cloneDeep(current.feedbackSettings.templated);

                    // if rubric criteria or options were removed, remove them from feedbackSettings.templated
                    Object.keys(newTemplated).forEach((criterionId) => {
                        if (!criterionIds.includes(criterionId)) {
                            delete newTemplated[criterionId];
                        } else {
                            const optionIds = rubric.criteria
                                .find(({ id }) => id === criterionId)
                                .options.map(({ id }) => id);
                            Object.keys(newTemplated[criterionId]).forEach((optionId) => {
                                if (!optionIds.includes(optionId)) {
                                    delete newTemplated[criterionId][optionId];
                                }
                            });
                        }
                    });

                    // check if any criteria or options were added
                    const wereNoAdditionsMade = rubric.criteria.every(({ id: criterionId, options }) => {
                        return (
                            Object.keys(newTemplated).includes(criterionId) &&
                            options.every(({ id: optionId }) => {
                                return Object.keys(newTemplated[criterionId]).includes(optionId);
                            })
                        );
                    });

                    if (!wereNoAdditionsMade) {
                        setErrors((current) => ({
                            ...current,
                            feedbackSettings: `(${t('needs-update', 'needs update')})`,
                        }));
                    }

                    newForm.feedbackSettings.templated = newTemplated;
                }

                let newGradingSettings = cloneDeep(current.gradingSettings);
                if (newGradingSettings) {
                    // remove keys that have been removed from the rubric
                    Object.keys(newGradingSettings.criteria).forEach((criterionId) => {
                        if (!criterionIds.includes(criterionId)) {
                            delete newGradingSettings.criteria[criterionId];
                        }
                    });

                    // check if any criteria were added
                    const wereNoAdditionsMade = rubric.criteria.every(({ id: criterionId }) => {
                        return Object.keys(newGradingSettings.criteria).includes(criterionId);
                    });

                    if (!wereNoAdditionsMade) {
                        setErrors((current) => ({
                            ...current,
                            gradingSettings: `(${t('needs-update', 'needs update')})`,
                        }));
                    }
                }

                return newForm;
            });
        },
        [setErrors, setForm, t]
    );

    const closeEditBaselineDialog = useCallback(() => setEditBaselineDialog({ open: false }), [setEditBaselineDialog]);

    const handleSaveBaseline = useCallback(
        (baseline) => {
            setForm((current) => ({ ...current, baseline }));
            setErrors((current) => omit(current, ['baseline']));
        },
        [setErrors, setForm]
    );

    const closeUploadContentBankResourcesDialog = useCallback(
        () => setUploadContentBankResourcesDialog({ open: false }),
        [setUploadContentBankResourcesDialog]
    );

    const onClickUploadContentBankResources = useCallback(
        (files) => {
            setForm((current) => ({
                ...current,
                contentBankResources: [...current.contentBankResources, ...files],
            }));
            setEditContentBankResourcesDialog({ open: true });
        },
        [setEditContentBankResourcesDialog, setForm]
    );

    const closeEditContentBankResourcesDialog = useCallback(
        () => setEditContentBankResourcesDialog({ open: false }),
        [setEditContentBankResourcesDialog]
    );

    const handleSaveContentBankResources = useCallback(
        (contentBankResources) => setForm((current) => ({ ...current, contentBankResources })),
        [setForm]
    );

    const closeFeedbackSettingsDialog = useCallback(
        () => setFeedbackSettingsDialog({ open: false }),
        [setFeedbackSettingsDialog]
    );

    const handleSaveFeedbackSettings = useCallback(
        (value) => {
            const { feedbackCustomContext } = value;
            value = omit(value, ['feedbackCustomContext']);
            setForm((current) => {
                return value.structure === 'templated'
                    ? {
                          ...current,
                          feedbackSettings: value,
                          aiGradingEnabled: true,
                          aiStudentFeedbackEnabled: false,
                          feedbackCustomContext,
                      }
                    : {
                          ...current,
                          feedbackSettings: value,
                          aiGradingEnabled: true,
                          feedbackCustomContext,
                      };
            });
            setErrors((current) => omit(current, ['feedbackSettings']));
        },
        [setErrors, setForm]
    );

    const closeInlineCommentsSettingsDialog = useCallback(
        () => setInlineCommentsSettingsDialog({ open: false }),
        [setInlineCommentsSettingsDialog]
    );

    const handleSaveInlineCommentsSettings = useCallback(
        (value) => {
            const { inlineCommentsCustomContext } = value;
            value = omit(value, ['inlineCommentsCustomContext']);
            setForm((current) => {
                return {
                    ...current,
                    inlineCommentsSettings: value,
                    inlineCommentsCustomContext,
                };
            });
        },
        [setForm]
    );

    const closeGradingSettingsDialog = useCallback(
        () => setGradingSettingsDialog({ open: false }),
        [setGradingSettingsDialog]
    );

    const handleSaveGradingSettings = useCallback(
        (value) => {
            const { gradingCustomContext } = value;
            const criteriaGradingInstructions = Object.entries(value.criteria).reduce(
                (acc, [criterionId, { grading_instructions }]) => {
                    return {
                        ...acc,
                        [criterionId]: { grading_instructions: grading_instructions || '' },
                    };
                },
                {}
            );
            const hasCriteriaGradingInstructions = Boolean(
                Object.values(criteriaGradingInstructions)
                    .map(({ grading_instructions }) => grading_instructions)
                    .reduce((acc, cur) => acc + cur, '')
            );
            setForm((current) => {
                return {
                    ...current,
                    gradingCustomContext,
                    gradingSettings: hasCriteriaGradingInstructions ? { criteria: criteriaGradingInstructions } : null,
                };
            });
            setErrors((current) => omit(current, ['gradingSettings']));
        },
        [setErrors, setForm]
    );

    const closeVideoDialog = useCallback(() => setVideoDialog({ open: false, videoId: '' }), []);

    useEffect(() => {
        setNode(
            <Stack direction={'row'} justifyContent={'flex-end'}>
                <SplitButton
                    loading={isSaving}
                    options={
                        assignment?.is_draft || location.pathname.includes('/assignments/new')
                            ? [
                                  { key: 'publish', value: t('common:controls.publish', 'Publish') },
                                  { key: 'save', value: t('common:controls.save-as-draft', 'Save as Draft') },
                              ]
                            : [
                                  { key: 'publish', value: t('common:controls.save', 'Save') },
                                  { key: 'save', value: t('common:controls.unpublish', 'Unpublish') },
                              ]
                    }
                    onClick={onClickSave}
                />
            </Stack>
        );

        return () => setNode(null);
    }, [assignment?.is_draft, isSaving, location.pathname, onClickSave, setNode, t]);

    const renderResponsiveLayout = (device) => {
        return {
            mobile: (
                <Mobile
                    batch={batch}
                    submissions={submissions}
                    openVideoAssignmentCreation={openVideoAssignmentCreation}
                    openVideoFeedbackSettings={openVideoFeedbackSettings}
                    openVideoGradingSettings={openVideoGradingSettings}
                    {...props}
                />
            ),
            tablet: (
                <Tablet
                    batch={batch}
                    submissions={submissions}
                    openVideoAssignmentCreation={openVideoAssignmentCreation}
                    openVideoFeedbackSettings={openVideoFeedbackSettings}
                    openVideoGradingSettings={openVideoGradingSettings}
                    {...props}
                />
            ),
            desktop: (
                <Desktop
                    batch={batch}
                    submissions={submissions}
                    openVideoAssignmentCreation={openVideoAssignmentCreation}
                    openVideoFeedbackSettings={openVideoFeedbackSettings}
                    openVideoGradingSettings={openVideoGradingSettings}
                    {...props}
                />
            ),
        }[device];
    };

    return isLoading || !form || !submissions ? (
        <Loading />
    ) : (
        <>
            {renderResponsiveLayout(device)}
            <SelectRubricDialog
                open={selectRubricDialog.open}
                onClose={closeSelectRubricDialog}
                onClickAddRubric={handleClickAddRubric}
                onClickUseRubric={handleClickUseRubric}
            />
            <CreateRubricDialog
                open={createRubricDialog.open}
                onClose={closeCreateRubricDialog}
                onClickCreateExisting={handleClickCreateExisting}
                onClickCreateAI={handleClickCreateAI}
                onClickCreateManual={handleClickCreateManual}
            />
            <CreateRubricExistingDialog
                open={createRubricExistingDialog.open}
                onClose={closeCreateRubricExistingDialog}
                onCreate={handleSaveRubric}
            />
            <CreateRubricAIDialog
                open={createRubricAIDialog.open}
                title={form.title}
                description={form.description}
                onClose={closeCreateRubricAIDialog}
                onCreate={handleSaveRubric}
            />
            <EditRubricDialog
                open={editRubricDialog.open}
                rubric={form.rubric}
                customDefaultForm={editRubricDialog.customDefaultForm}
                onClose={closeEditRubricDialog}
                onSave={handleSaveRubric}
            />
            <EditBaselineDialog
                open={editBaselineDialog.open}
                rubric={form.rubric}
                baseline={form.baseline}
                onClose={closeEditBaselineDialog}
                onSave={handleSaveBaseline}
            />
            <UploadContentBankResourcesDialog
                open={uploadContentBankResourcesDialog.open}
                resources={form.contentBankResources}
                onClose={closeUploadContentBankResourcesDialog}
                onClick={onClickUploadContentBankResources}
            />
            <EditContentBankResourcesDialog
                open={editContentBankResourcesDialog.open}
                resources={form.contentBankResources}
                onClose={closeEditContentBankResourcesDialog}
                onSave={handleSaveContentBankResources}
            />
            <FeedbackSettingsDialog
                open={feedbackSettingsDialog.open}
                form={form}
                onClose={closeFeedbackSettingsDialog}
                onSave={handleSaveFeedbackSettings}
            />
            <InlineCommentsSettingsDialog
                open={inlineCommentsSettingsDialog.open}
                form={form}
                onClose={closeInlineCommentsSettingsDialog}
                onSave={handleSaveInlineCommentsSettings}
            />
            <EditGradingSettingsDialog
                open={gradingSettingsDialog.open}
                form={form}
                onClose={closeGradingSettingsDialog}
                onSave={handleSaveGradingSettings}
            />
            <VideoDialog open={videoDialog.open} videoId={videoDialog.videoId} onClose={closeVideoDialog} />
        </>
    );
};

export default PageLayout;
